import { emptyScopeSheet, emptyBalcony } from './scopeSheetDefaults';

const STATUS = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  COMPLETED: 'completed',
};

const deepMerge = (base, patch) => {
  if (Array.isArray(patch)) return patch;
  if (patch && typeof patch === 'object') {
    const out = { ...(base && typeof base === 'object' ? base : {}) };
    Object.keys(patch).forEach((key) => {
      out[key] = deepMerge(base?.[key], patch[key]);
    });
    return out;
  }
  return patch;
};

const withScope = (patch) => deepMerge(emptyScopeSheet(), patch);

const roomDims = (length, width, ceilingHeight) => ({
  length,
  width,
  ceilingHeight,
});

const windowBlock = (count, sill, lintel, types) => ({
  windowCount: count,
  sillHeight: sill,
  lintelHeight: lintel,
  windowType: types,
});

const bedroomBlock = (dims, extras = {}) => ({
  basicInfo: {
    ...dims,
    ...windowBlock('2', "3'", "7'", ['Sliding', 'Openable']),
    balconyAccess: extras.balconyAccess || 'Yes',
    balconyDoor: extras.balconyDoor || ['Sliding'],
    balconyDoorWidth: extras.balconyDoorWidth || "7'",
    balconyDoorHeight: extras.balconyDoorHeight || "7'",
    balconyRailing: extras.balconyRailing || ['Glass'],
    balconyRailingSize: extras.balconyRailingSize || '10 r.ft',
    notes: extras.basicNotes || '',
  },
  civil: {
    items: [
      'New flooring installation',
      'Skirting installation',
      'Floor leveling / screeding',
    ],
    notes: '',
  },
  falseCeiling: {
    required: 'Yes',
    types: ['POP'],
    coveLighting: ['Outside Cove Only'],
    ceilingDesign: ['Grooves'],
    notes: '',
  },
  floorCovering: {
    materials: ['Gloss Tile / Matt Tile'],
    notes: '',
  },
  softFurnishing: {
    curtains: 'Sheer + Blackout',
    windowCovering: ['Curtains'],
    rug: extras.rug || 'Yes',
    upholstery: extras.upholstery || '',
    notes: '',
  },
  carpentry: {
    wallPanellingRequired: extras.wallPanellingRequired || 'Yes',
    numberOfWalls: extras.numberOfWalls || '1',
    wallMaterials: extras.wallMaterials || ['Fabric Wrapped'],
    wallSize1: extras.wallSize1 || "11'",
    wallSize2: '',
    wallSize3: '',
    notes: '',
  },
  wardrobe: {
    wardrobeType: extras.wardrobeType || ['Normal Wardrobe'],
    wardrobeHeight: extras.wardrobeHeight || ['Full Height'],
    wardrobeWidth: extras.wardrobeWidth || "8'",
    wardrobeDepth: extras.wardrobeDepth || "2'",
    loftHeight: extras.loftHeight || "2'",
    internalLayout: [
      'Hanging Rod',
      'Shelves',
      'Drawers',
      'Shoe Rack',
      'Sensor Light',
    ],
    shelvesQty: extras.shelvesQty || '4',
    drawersQty: extras.drawersQty || '3',
    hardwareLevel: ['Ultra Premium (Blum Soft-Close)'],
    notes: extras.wardrobeNotes || '',
  },
  electrical: {
    wiringBrand: ['Havells'],
    wireType: ['FR'],
    switches: ['Legrand Myrius'],
    lightCategories: ['Downlights', 'Cove Lights'],
    wallLightsQty: '2',
    fansQty: '1',
    acWiringQty: '1',
    speakersRequired: 'No',
    automationRequired: extras.automation || 'Yes',
    automationItems: extras.automationItems || ['Lights', 'AC'],
    acTypes: ['Split'],
    approxWiringLength: '25 m',
    notes: '',
  },
  paint: {
    wallPaint: ['Royale Matt'],
    ceilingPaint: ['Plastic Premium'],
    notes: extras.paintNotes || '',
  },
  washroomBasic: {
    ...roomDims("7'", "4'", "8'6\""),
    ...windowBlock('1', "4'", "7'", ['Fixed']),
    notes: '',
  },
  washroomCivil: {
    items: ['Waterproofing', 'Replace Fittings Only'],
    notes: '',
  },
  washroomWalls: {
    materials: ['Tile'],
    groutColor: 'Light grey',
    notes: '',
  },
  washroomFloor: {
    materials: ['Anti-Skid Tile'],
    groutColor: 'Light grey',
    notes: '',
  },
  washroomBasin: {
    basinType: ['Wall-Mounted'],
    notes: '',
  },
  washroomShower: {
    showerType: ['Wall-Mounted', 'Shower Panel'],
    notes: '',
  },
  washroomFittings: {
    fittingBrands: ['Jaquar'],
    fittingOther: '',
    plumbingMaterial: ['Ashirvad'],
    notes: '',
  },
  washroomElectrical: {
    items: ['Mirror Light', 'Exhaust Fan', 'Geyser'],
    wiringBrand: ['Havells'],
    wireType: ['FR'],
    switchType: ['Legrand Myrius'],
    ceilingPaint: ['Plastic Premium'],
    notes: '',
  },
});

/** Project 1 — 3BHK Pune apartment (full sample pack) */
export const buildMehtaScopeSheet = () =>
  withScope({
    A: {
      basicInfo: {
        projectAddress: '12 Palm Grove, Koregaon Park, Pune 411001',
        clientNames: 'Rahul & Priya Mehta',
        propertyType: ['Apartment'],
        unitType: '3BHK',
        totalCarpetArea: '1450 sq.ft',
        notes: 'Site visit done on 15 Mar 2026',
      },
      ceilingHeight: {
        generalCeilingHeight: "9'6\"",
        floorToFloorHeight: "10'6\"",
        foyerCeilingHeight: "9'6\"",
        livingCeilingHeight: "10'",
        diningCeilingHeight: "9'6\"",
        kitchenCeilingHeight: "9'",
        bedroom1CeilingHeight: "9'6\"",
        bedroom2CeilingHeight: "9'6\"",
        bedroom3CeilingHeight: "9'",
        storeRoomCeilingHeight: "8'6\"",
        helpRoomCeilingHeight: "8'",
        notes: '',
      },
      windowInfo: {
        windowCount: '2–3 per room',
        sillHeight: "3'",
        lintelHeight: "7'",
        windowType: ['Sliding', 'Openable'],
        notes: '',
      },
    },
    B: {
      servicesRequired: {
        selected: [
          'Interior Fit-Out',
          'Project Management',
          'Material Selection Support',
        ],
        notes: 'Turnkey interiors excluding structural changes',
      },
    },
    C: {
      deliverables: {
        selected: [
          '2D Layouts',
          'Furniture Layout',
          'Electrical Layout',
          '3D Renders',
          'BOQ',
          'Site Visits',
        ],
        notes: '3D only for living + master bedroom',
      },
    },
    D: {
      basicInfo: {
        ...roomDims("6'", "4'", "9'6\""),
        notes: 'Teak finish preferred',
      },
      doorSystem: {
        doorTypes: ['Main Wooden Door', 'Safety Door'],
        doorWidth: "3'6\"",
        doorHeight: "7'",
        notes: '',
      },
      carpentry: {
        items: ['Shoe Rack', 'Mirror'],
        wallPanelMaterials: ['Laminate'],
        notes: '',
      },
      electrical: {
        items: ['Wall Light', 'Bell Wiring'],
        ceilingLightsQty: '2',
        notes: '',
      },
      decor: {
        items: ['Rugs/Mats', 'Entrance Flooring'],
        notes: '',
      },
    },
    E: {
      basicInfo: {
        ...roomDims("8'", "5'", "9'6\""),
        ...windowBlock('1', "3'", "7'", ['Fixed']),
        balconyAccess: 'No',
        balconyDoor: [],
        balconyDoorWidth: '',
        balconyDoorHeight: '',
        balconyRailing: [],
        balconyRailingSize: '',
        notes: '',
      },
      civil: {
        items: ['New flooring installation', 'Skirting installation'],
        notes: '',
      },
      falseCeiling: {
        required: 'Yes',
        types: ['POP'],
        coveLighting: ['Outside Cove Only'],
        ceilingDesign: ['No Design'],
        notes: '',
      },
      floorCovering: {
        materials: ['Gloss Tile / Matt Tile'],
        notes: '',
      },
      softFurnishing: {
        curtains: 'Sheer Only',
        windowCovering: ['Curtains'],
        notes: '',
      },
      carpentry: {
        wallPanellingRequired: 'No',
        numberOfWalls: '',
        wallMaterials: [],
        wallSize1: '',
        wallSize2: '',
        wallSize3: '',
        consoleStorage: ['Console Table', 'Shoe Storage'],
        carpentryMaterial: 'HDHMR',
        hardwareLevel: ['Mid (Hettich India / Ozone / Inox)'],
        notes: '',
      },
      electrical: {
        wiringBrand: ['Havells'],
        wireType: ['FR'],
        switches: ['Legrand Myrius'],
        lightCategories: ['Downlights', 'Cove Lights'],
        wallLightsQty: '2',
        fansQty: '0',
        acWiringQty: '0',
        speakersRequired: 'No',
        automationRequired: 'No',
        automationItems: [],
        acTypes: [],
        approxWiringLength: '10 m',
        additionalElectrical: ['Console LED Strip'],
        entranceBell: ['Smart Bell (WiFi)'],
        notes: '',
      },
      paint: {
        wallPaint: ['Royale Matt'],
        ceilingPaint: ['Plastic Premium'],
        notes: '',
      },
    },
    F: {
      basicInfo: {
        ...roomDims("18'", "12'", "10'"),
        ...windowBlock('3', "3'", "7'", ['Sliding', 'Openable']),
        balconyAccess: 'Yes',
        balconyDoor: ['Sliding'],
        balconyDoorWidth: "7'",
        balconyDoorHeight: "7'",
        balconyRailing: ['Glass'],
        balconyRailingSize: '12 r.ft',
        notes: '',
      },
      civil: {
        items: [
          'Floor leveling / screeding',
          'New flooring installation',
          'Skirting installation',
        ],
        notes: '',
      },
      falseCeiling: {
        required: 'Yes',
        types: ['POP'],
        coveLighting: ['Inside + Outside Cove'],
        ceilingDesign: ['Grooves', 'Mouldings'],
        notes: '',
      },
      floorCovering: {
        materials: ['Gloss Tile / Matt Tile'],
        notes: 'Matt tile preferred',
      },
      softFurnishing: {
        curtains: 'Sheer + Blackout',
        windowCovering: ['Curtains', 'Blinds'],
        rug: 'Yes',
        upholstery: 'Fabric sofa in warm grey',
        notes: '',
      },
      carpentry: {
        wallPanellingRequired: 'Yes',
        numberOfWalls: '1',
        wallMaterials: ['Veneer'],
        wallSize1: "12'",
        wallSize2: '',
        wallSize3: '',
        furnitureUnits: ['TV Unit', 'Console'],
        sofaSeater: '3 seater',
        armchairs: '2',
        loungeChairs: '1',
        coffeeTable: 'Yes',
        sideTables: 'Yes',
        rug: 'Yes',
        hardwareLevel: ['Premium (Hafele / Hettich Germany)'],
        notes: '',
      },
      electrical: {
        wiringBrand: ['Havells'],
        wireType: ['FRLS'],
        switches: ['Schneider Norysis'],
        lightCategories: ['COB', 'Downlights', 'Cove Lights', 'Profile Lights'],
        wallLightsQty: '4',
        fansQty: '2',
        acWiringQty: '1',
        speakersRequired: 'Yes',
        automationRequired: 'Yes',
        automationItems: ['Lights', 'Curtains', 'AC', 'Speakers'],
        acTypes: ['Split'],
        approxWiringLength: '40 m',
        chandelier: 'Yes',
        notes: '',
      },
      paint: {
        wallPaint: ['Royale Shine'],
        ceilingPaint: ['Royale Matt'],
        notes: '',
      },
    },
    G: {
      basicInfo: {
        ...roomDims("12'", "10'", "9'6\""),
        ...windowBlock('2', "3'", "7'", ['Openable']),
        balconyAccess: 'No',
        balconyDoor: [],
        balconyDoorWidth: '',
        balconyDoorHeight: '',
        balconyRailing: [],
        balconyRailingSize: '',
        notes: '',
      },
      civil: {
        items: ['New flooring installation', 'Skirting installation'],
        notes: '',
      },
      falseCeiling: {
        required: 'Yes',
        types: ['POP'],
        coveLighting: ['Outside Cove Only'],
        ceilingDesign: ['Beam Covering'],
        notes: '',
      },
      floorCovering: {
        materials: ['Marble'],
        notes: '',
      },
      softFurnishing: {
        curtains: 'Sheer + Blackout',
        windowCovering: ['Curtains'],
        rug: '',
        upholstery: '',
        notes: '',
      },
      carpentry: {
        wallPanellingRequired: 'No',
        numberOfWalls: '',
        wallMaterials: [],
        wallSize1: '',
        wallSize2: '',
        wallSize3: '',
        diningStorage: ['Crockery Unit', 'Buffet / Sideboard'],
        diningTablePersons: '6',
        chairs: '6',
        benchSeating: 'No',
        hardwareLevel: ['Mid (Hettich India / Ozone / Inox)'],
        notes: '',
      },
      electrical: {
        wiringBrand: ['Polycab'],
        wireType: ['FR'],
        switches: ['Anchor Roma'],
        lightCategories: ['Downlights', 'Cove Lights'],
        wallLightsQty: '2',
        fansQty: '1',
        acWiringQty: '0',
        speakersRequired: 'No',
        automationRequired: 'No',
        automationItems: [],
        acTypes: [],
        approxWiringLength: '15 m',
        chandelier: 'Yes',
        notes: '',
      },
      paint: {
        wallPaint: ['Royale Matt'],
        ceilingPaint: ['Plastic Premium'],
        notes: '',
      },
    },
    H: {
      basicInfo: {
        ...roomDims("12'", "8'", "9'"),
        ...windowBlock('1', "3'6\"", "7'", ['Sliding']),
        balconyAccess: 'Yes',
        balconyDoor: ['Sliding'],
        balconyDoorWidth: "4'",
        balconyDoorHeight: "7'",
        balconyRailing: ['MS'],
        balconyRailingSize: '4 r.ft',
        notes: '',
      },
      civil: {
        items: [
          'New flooring installation',
          'Skirting installation',
          'New partitions / wall shifting',
        ],
        notes: '',
      },
      falseCeiling: {
        required: 'Yes',
        types: ['POP'],
        coveLighting: ['Inside Cove Only'],
        ceilingDesign: ['No Design'],
        notes: '',
      },
      floorCovering: {
        materials: ['Gloss Tile / Matt Tile'],
        notes: '',
      },
      constructionType: {
        types: ['Full Modular Factory-Made Kitchen'],
        notes: '',
      },
      carpentry: {
        carcassMaterial: ['HDHMR'],
        shutterFinish: ['Laminate'],
        hardwareLevel: ['Premium (Hafele / Hettich Germany)'],
        fittings: [
          'Tandem Drawers',
          'Bottle Pullout',
          'Cutlery Tray',
          'Integrated Dustbin',
        ],
        cornerUnitQty: '1',
        tandemDrawersQty: '3',
        bottlePulloutQty: '1',
        counter: ['Quartz'],
        backsplashMaterial: 'White subway tile',
        notes: '',
      },
      electrical: {
        wiringBrand: ['Havells'],
        wireType: ['FRLS'],
        switches: ['Legrand Myrius'],
        lightCategories: [
          'Under-Cabinet LED',
          'Ceiling Lights',
          'Profile Lights',
        ],
        fansQty: '1',
        acWiringQty: '0',
        speakersRequired: 'No',
        acTypes: [],
        approxWiringLength: '20 m',
        heavyAppliances: [
          'Hob Point',
          'Chimney + Duct Route',
          'Refrigerator',
          'Aquaguard / RO',
        ],
        notes: '',
      },
      paint: {
        ceilingPaint: ['Plastic Premium'],
        notes: '',
      },
      plumbing: {
        sinkRelocation: 'No',
        drainLineChange: 'No',
        dishwasherPlumbing: 'No',
        geyserRequired: 'Yes',
        roLocation: 'Under-Sink',
        plumbingBrand: ['Astral'],
        notes: '',
      },
    },
    'bedroom-1': bedroomBlock(roomDims("14'", "11'", "9'6\""), {
      wardrobeType: ['Walk-In Wardrobe'],
      wardrobeHeight: ['Full Height'],
      wardrobeWidth: "8'",
      wallMaterials: ['Fabric Wrapped'],
      paintNotes: 'Master bedroom – warm neutrals',
    }),
    'bedroom-2': bedroomBlock(roomDims("12'", "10'", "9'6\""), {
      wardrobeType: ['Normal Wardrobe'],
      wardrobeHeight: ['8 ft + Loft'],
      loftHeight: "2'",
      wallPanellingRequired: 'No',
      wallMaterials: [],
      numberOfWalls: '',
    }),
    'bedroom-3': bedroomBlock(roomDims("11'", "10'", "9'"), {
      wardrobeType: ['Normal Wardrobe'],
      wardrobeHeight: ['8 ft'],
      paintNotes: 'Kids room – soft pastel walls',
      upholstery: 'Kids theme soft furnishings',
      automation: 'No',
      automationItems: [],
    }),
    'help-room': {
      basicInfo: {
        length: "8'",
        width: "6'",
        height: "8'",
        wardrobeDimensions: "4' × 6'",
        material: 'HDHMR',
        finish: 'Laminate',
        hardwareLevel: ['Basic (Local Indian Brands)'],
        bedNiche: 'Yes',
        notes: '',
      },
      electrical: {
        wiringBrand: ['Local'],
        wireType: ['FR'],
        switches: ['Anchor Roma'],
        fan: 'Yes',
        lights: '2 ceiling lights',
        ventilation: 'Yes',
        paint: ['Plastic Premium'],
        notes: '',
      },
      helpBathroom: {
        tilesWallCovering: 'Ceramic tile',
        floorCovering: 'Anti-skid tile',
        basin: ['Wall-Mounted'],
        fittings: ['Cera'],
        plumbingMaterial: ['Supreme'],
        exhaust: 'Yes',
        switches: ['Anchor Roma'],
        geyser: 'Yes',
        paint: ['Plastic Premium'],
        notes: '',
      },
    },
    'store-room': {
      basicInfo: {
        ...roomDims("6'", "4'", "8'6\""),
        notes: '',
      },
      storeWardrobe: {
        width: "5'",
        height: "7'",
        material: ['Laminate'],
        carcass: ['HDHMR'],
        hardwareLevel: ['Mid (Hettich India / Ozone / Inox)'],
        lofts: 'Yes',
        shelves: 'Yes',
        lighting: 'Yes',
        ventilation: 'Yes',
        paint: ['Plastic Premium'],
        wiringBrand: ['Polycab'],
        switches: ['GM'],
        notes: '',
      },
    },
    balconies: {
      balconies: [
        {
          ...emptyBalcony(),
          label: 'Living balcony',
          basic: {
            length: "10'",
            width: "4'",
            ceilingHeight: "9'",
            doorType: ['Sliding'],
            doorSize: "7' × 7'",
            railingType: ['Glass'],
            railingHeight: "3'6\"",
            notes: '',
          },
          civil: {
            items: ['Waterproofing Required', 'Slope Correction'],
            notes: '',
          },
          floor: {
            materials: ['Anti-Skid Tile'],
            tileSize: "2'×1'",
            groutColor: 'Grey',
            notes: '',
          },
          walls: {
            materials: ['Exterior Paint'],
            featureWallMaterial: '',
            notes: '',
          },
          ceiling: {
            finishes: ['Exterior Paint'],
            waterproofCoat: 'Yes',
            lighting: 'Yes',
            fanPoint: 'No',
            notes: '',
          },
          carpentry: {
            items: ['Planter Box (Built-In)', 'Seating Bench'],
            seatingMaterial: 'WPC',
            storageMaterial: '',
            privacyMaterial: '',
            notes: '',
          },
          looseFurniture: {
            outdoorChairsQty: '2',
            outdoorTableQty: '1',
            outdoorSofa: 'No',
            swing: 'No',
            swingType: '',
            weatherproofCushions: 'Yes',
            notes: '',
          },
          electrical: {
            items: ['Wall Lamps', 'Outdoor Spotlights'],
            notes: '',
          },
          plumbing: {
            waterTap: 'Yes',
            tapType: 'Garden Tap',
            drainPosition: 'Corner left',
            rerouting: 'No',
            notes: '',
          },
          greenery: {
            items: [
              'Pots Required (Small / Medium / Large)',
              'Drip Irrigation',
            ],
            material: 'Ceramic pots',
            notes: '',
          },
          safety: {
            items: ['Child Safety Grill', 'Bird Net'],
            notes: '',
          },
        },
      ],
    },
  });

/** Project 2 — 2BHK villa, Bengaluru */
export const buildSharmaScopeSheet = () =>
  withScope({
    A: {
      basicInfo: {
        projectAddress: '88 Whitefield Main Road, Bengaluru 560066',
        clientNames: 'Ananya Sharma',
        propertyType: ['Villa'],
        unitType: '2BHK',
        totalCarpetArea: '1180 sq.ft',
        notes: 'New villa interiors – first fit-out',
      },
      ceilingHeight: {
        generalCeilingHeight: "10'",
        floorToFloorHeight: "11'",
        foyerCeilingHeight: "10'",
        livingCeilingHeight: "10'6\"",
        diningCeilingHeight: "10'",
        kitchenCeilingHeight: "9'6\"",
        bedroom1CeilingHeight: "10'",
        bedroom2CeilingHeight: "10'",
        storeRoomCeilingHeight: "9'",
        helpRoomCeilingHeight: "8'6\"",
        notes: '',
      },
      windowInfo: {
        windowCount: '2 per room',
        sillHeight: "3'",
        lintelHeight: "7'",
        windowType: ['Openable', 'Fixed'],
        notes: '',
      },
    },
    B: {
      servicesRequired: {
        selected: [
          'Full Home Renovation',
          'Civil + Interior Turnkey',
          'Furniture + Styling',
        ],
        notes: 'Includes light civil work',
      },
    },
    C: {
      deliverables: {
        selected: [
          '2D Layouts',
          'Working Drawings',
          'Material Boards',
          'BOQ',
          'Vendor Coordination',
          'Final Styling',
        ],
        notes: '',
      },
    },
    D: {
      basicInfo: {
        ...roomDims("7'", "5'", "10'"),
        notes: '',
      },
      doorSystem: {
        doorTypes: ['Two-Door System', 'Safety Door'],
        doorWidth: "4'",
        doorHeight: "7'",
        notes: '',
      },
      carpentry: {
        items: ['Shoe Rack', 'Seating Near Shoerack', 'Mirror'],
        wallPanelMaterials: ['Stone Cladding'],
        notes: '',
      },
      electrical: {
        items: ['Wall Light', 'Ceiling Lights', 'Staircase Light'],
        ceilingLightsQty: '3',
        notes: '',
      },
      decor: {
        items: ['Planters', 'Wall decor', 'Entrance Flooring'],
        notes: '',
      },
    },
    E: {
      basicInfo: {
        ...roomDims("9'", "6'", "10'"),
        ...windowBlock('2', "3'", "7'", ['Openable']),
        balconyAccess: 'No',
        balconyDoor: [],
        balconyDoorWidth: '',
        balconyDoorHeight: '',
        balconyRailing: [],
        balconyRailingSize: '',
        notes: '',
      },
      civil: {
        items: ['Demolition of flooring', 'New flooring installation'],
        notes: '',
      },
      falseCeiling: {
        required: 'Yes',
        types: ['Wooden'],
        coveLighting: ['Inside + Outside Cove'],
        ceilingDesign: ['Mouldings'],
        notes: '',
      },
      floorCovering: {
        materials: ['Marble'],
        notes: '',
      },
      softFurnishing: {
        curtains: 'Blackout Only',
        windowCovering: ['Blinds'],
        notes: '',
      },
      carpentry: {
        wallPanellingRequired: 'Yes',
        numberOfWalls: '2',
        wallMaterials: ['Veneer', 'Stone Cladding'],
        wallSize1: "8'",
        wallSize2: "6'",
        wallSize3: '',
        consoleStorage: ['Mirror Unit', 'Coat Hanger'],
        carpentryMaterial: 'Plywood',
        hardwareLevel: ['Premium (Hafele / Hettich Germany)'],
        notes: '',
      },
      electrical: {
        wiringBrand: ['Polycab'],
        wireType: ['FRLS'],
        switches: ['Schneider Norysis'],
        lightCategories: ['COB', 'Profile Lights'],
        wallLightsQty: '3',
        fansQty: '0',
        acWiringQty: '0',
        speakersRequired: 'No',
        automationRequired: 'Yes',
        automationItems: ['Lights'],
        acTypes: [],
        approxWiringLength: '12 m',
        entranceBell: ['Bell With Video Screen / Digital Door Camera'],
        notes: '',
      },
      paint: {
        wallPaint: ['Royale Shine'],
        ceilingPaint: ['PU'],
        notes: '',
      },
    },
    F: {
      basicInfo: {
        ...roomDims("16'", "13'", "10'6\""),
        ...windowBlock('4', "3'", "7'", ['Sliding']),
        balconyAccess: 'Yes',
        balconyDoor: ['Hinged Double'],
        balconyDoorWidth: "6'",
        balconyDoorHeight: "7'",
        balconyRailing: ['Aluminium + Glass'],
        balconyRailingSize: '14 r.ft',
        notes: '',
      },
      civil: {
        items: [
          'New partitions / wall shifting',
          'New flooring installation',
          'Beam/column covering',
        ],
        notes: '',
      },
      falseCeiling: {
        required: 'Yes',
        types: ['POP', 'Wooden'],
        coveLighting: ['Inside + Outside Cove'],
        ceilingDesign: ['Grooves', 'Beam Covering'],
        notes: '',
      },
      floorCovering: {
        materials: ['Marble'],
        notes: '',
      },
      softFurnishing: {
        curtains: 'Sheer + Blackout',
        windowCovering: ['Curtains'],
        rug: 'Large living rug',
        upholstery: 'Leather accent chairs',
        notes: '',
      },
      carpentry: {
        wallPanellingRequired: 'Yes',
        numberOfWalls: '2',
        wallMaterials: ['PU Coated', 'Glass'],
        wallSize1: "13'",
        wallSize2: "10'",
        wallSize3: '',
        furnitureUnits: ['TV Unit', 'Bar Unit', 'Window Seating'],
        sofaSeater: 'L-shape 5 seater',
        armchairs: '2',
        loungeChairs: '2',
        coffeeTable: 'Yes',
        sideTables: 'Yes',
        rug: 'Yes',
        hardwareLevel: ['Ultra Premium (Blum Soft-Close)'],
        notes: '',
      },
      electrical: {
        wiringBrand: ['Havells'],
        wireType: ['FRLS'],
        switches: ['Premium Smart Switches'],
        lightCategories: ['COB', 'Downlights', 'Cove Lights', 'Panel Lights'],
        wallLightsQty: '6',
        fansQty: '2',
        acWiringQty: '2',
        speakersRequired: 'Yes',
        automationRequired: 'Yes',
        automationItems: ['Lights', 'Curtains', 'AC', 'TV', 'Speakers'],
        acTypes: ['Cassette'],
        approxWiringLength: '55 m',
        chandelier: 'Yes',
        notes: '',
      },
      paint: {
        wallPaint: ['Texture'],
        ceilingPaint: ['Royale Shine'],
        notes: '',
      },
    },
    G: {
      basicInfo: {
        ...roomDims("11'", "9'", "10'"),
        ...windowBlock('2', "3'", "7'", ['Openable']),
        balconyAccess: 'No',
        balconyDoor: [],
        balconyDoorWidth: '',
        balconyDoorHeight: '',
        balconyRailing: [],
        balconyRailingSize: '',
        notes: '',
      },
      civil: {
        items: ['New flooring installation'],
        notes: '',
      },
      falseCeiling: {
        required: 'Yes',
        types: ['POP'],
        coveLighting: ['Outside Cove Only'],
        ceilingDesign: ['No Design'],
        notes: '',
      },
      floorCovering: {
        materials: ['Granite'],
        notes: '',
      },
      softFurnishing: {
        curtains: 'Sheer Only',
        windowCovering: ['Curtains'],
        notes: '',
      },
      carpentry: {
        wallPanellingRequired: 'No',
        numberOfWalls: '',
        wallMaterials: [],
        wallSize1: '',
        wallSize2: '',
        wallSize3: '',
        diningStorage: ['Console Table'],
        diningTablePersons: '4',
        chairs: '4',
        benchSeating: 'Yes',
        hardwareLevel: ['Mid (Hettich India / Ozone / Inox)'],
        notes: '',
      },
      electrical: {
        wiringBrand: ['Finolex'],
        wireType: ['FR'],
        switches: ['GM'],
        lightCategories: ['Downlights'],
        wallLightsQty: '2',
        fansQty: '1',
        acWiringQty: '0',
        speakersRequired: 'No',
        automationRequired: 'No',
        automationItems: [],
        acTypes: [],
        approxWiringLength: '12 m',
        chandelier: 'No',
        notes: '',
      },
      paint: {
        wallPaint: ['Satin'],
        ceilingPaint: ['Plastic Premium'],
        notes: '',
      },
    },
    H: {
      basicInfo: {
        ...roomDims("11'", "9'", "9'6\""),
        ...windowBlock('2', "3'", "7'", ['Sliding']),
        balconyAccess: 'Yes',
        balconyDoor: ['Sliding'],
        balconyDoorWidth: "5'",
        balconyDoorHeight: "7'",
        balconyRailing: ['SS'],
        balconyRailingSize: '5 r.ft',
        notes: '',
      },
      civil: {
        items: ['Door shifting / Door Enlargement / Add Extra Door'],
        notes: '',
      },
      falseCeiling: {
        required: 'Yes',
        types: ['Grid'],
        coveLighting: ['Inside Cove Only'],
        ceilingDesign: ['No Design'],
        notes: '',
      },
      floorCovering: {
        materials: ['Gloss Tile / Matt Tile'],
        notes: '',
      },
      constructionType: {
        types: ['Carcass On-Site + Factory-Made Shutters'],
        notes: '',
      },
      carpentry: {
        carcassMaterial: ['BWP Entire Kitchen'],
        shutterFinish: ['Acrylic'],
        hardwareLevel: ['Ultra Premium (Blum Soft-Close)'],
        fittings: [
          'Corner Unit',
          'Pantry Unit',
          'Grain Trolley',
          'Wicker Basket',
          'Glass Tray / Plate Tray',
        ],
        cornerUnitQty: '1',
        tandemDrawersQty: '4',
        bottlePulloutQty: '2',
        counter: ['Granite'],
        backsplashMaterial: 'Glass mosaic',
        notes: '',
      },
      electrical: {
        wiringBrand: ['Havells'],
        wireType: ['FRLS'],
        switches: ['Schneider Norysis'],
        lightCategories: ['Under-Cabinet LED', 'Panel Lights', 'Cove Lights'],
        fansQty: '1',
        acWiringQty: '1',
        speakersRequired: 'No',
        acTypes: ['Split'],
        approxWiringLength: '22 m',
        heavyAppliances: [
          'Hob Point',
          'Chimney + Duct Route',
          'Microwave',
          'Oven',
          'Dishwasher Electrical Point',
          'Aquaguard / RO',
        ],
        notes: '',
      },
      paint: {
        ceilingPaint: ['Royale Matt'],
        notes: '',
      },
      plumbing: {
        sinkRelocation: 'Yes',
        drainLineChange: 'Yes',
        dishwasherPlumbing: 'Yes',
        geyserRequired: 'Yes',
        roLocation: 'Over-Head',
        plumbingBrand: ['Ashirvad'],
        notes: '',
      },
    },
    'bedroom-1': bedroomBlock(roomDims("15'", "12'", "10'"), {
      wardrobeType: ['Walk-In Wardrobe'],
      wardrobeHeight: ['Full Height'],
      wallMaterials: ['Veneer'],
      balconyRailing: ['Aluminium + Glass'],
    }),
    'bedroom-2': bedroomBlock(roomDims("12'", "11'", "10'"), {
      wardrobeType: ['Normal Wardrobe'],
      wardrobeHeight: ['8 ft + Loft'],
      wallPanellingRequired: 'Yes',
      wallMaterials: ['Laminate'],
      numberOfWalls: '1',
    }),
    'help-room': {
      basicInfo: {
        length: "7'",
        width: "6'",
        height: "8'6\"",
        wardrobeDimensions: "3' × 6'",
        material: 'Ply',
        finish: 'Laminate',
        hardwareLevel: ['Basic (Local Indian Brands)'],
        bedNiche: 'No',
        notes: '',
      },
      electrical: {
        wiringBrand: ['Local'],
        wireType: ['Non-FR'],
        switches: ['Anchor Roma'],
        fan: 'Yes',
        lights: '1 ceiling + 1 wall',
        ventilation: 'Yes',
        paint: ['Plastic Premium'],
        notes: '',
      },
      helpBathroom: {
        tilesWallCovering: 'Ceramic',
        floorCovering: 'Anti-skid',
        basin: ['Over-Counter'],
        fittings: ['Hindware'],
        plumbingMaterial: ['Prakash'],
        exhaust: 'Yes',
        switches: ['GM'],
        geyser: 'Yes',
        paint: ['Plastic Premium'],
        notes: '',
      },
    },
    'store-room': {
      basicInfo: {
        ...roomDims("5'", "4'", "9'"),
        notes: '',
      },
      storeWardrobe: {
        width: "4'",
        height: "7'",
        material: ['PU'],
        carcass: ['BWP'],
        hardwareLevel: ['Basic (Local Indian Brands)'],
        lofts: 'Yes',
        shelves: 'Yes',
        lighting: 'Yes',
        ventilation: 'No',
        paint: ['Plastic Premium'],
        wiringBrand: ['Local'],
        switches: ['Anchor Roma'],
        notes: '',
      },
    },
    balconies: {
      balconies: [
        {
          ...emptyBalcony(),
          label: 'Master bedroom balcony',
          basic: {
            length: "8'",
            width: "3'6\"",
            ceilingHeight: "9'6\"",
            doorType: ['Hinged Single'],
            doorSize: "3' × 7'",
            railingType: ['SS'],
            railingHeight: "3'6\"",
            notes: '',
          },
          civil: {
            items: ['Waterproofing Required', 'PCC / Leveling'],
            notes: '',
          },
          floor: {
            materials: ['Wooden Tile'],
            tileSize: "2'×2'",
            groutColor: 'Beige',
            notes: '',
          },
          walls: {
            materials: ['Texture Paint'],
            featureWallMaterial: '',
            notes: '',
          },
          ceiling: {
            finishes: ['Wooden Slats'],
            waterproofCoat: 'Yes',
            lighting: 'Yes',
            fanPoint: 'Yes',
            notes: '',
          },
          carpentry: {
            items: ['Seating Bench', 'Outdoor Cabinet'],
            seatingMaterial: 'Wood',
            storageMaterial: 'Exterior Ply',
            privacyMaterial: '',
            notes: '',
          },
          looseFurniture: {
            outdoorChairsQty: '2',
            outdoorTableQty: '1',
            outdoorSofa: 'No',
            swing: 'Yes',
            swingType: 'Wood',
            weatherproofCushions: 'Yes',
            notes: '',
          },
          electrical: {
            items: ['Outdoor Strip Lights', 'Fan Point'],
            notes: '',
          },
          plumbing: {
            waterTap: 'No',
            tapType: '',
            drainPosition: 'Existing corner',
            rerouting: 'No',
            notes: '',
          },
          greenery: {
            items: ['Vertical Garden'],
            material: 'Metal frame + planter boxes',
            notes: '',
          },
          safety: {
            items: [
              'Child Safety Grill',
              'Check Railing Height Compliance',
            ],
            notes: '',
          },
        },
      ],
    },
  });

export const DETAILED_SEED_PROJECTS = [
  {
    id: 'proj_seed_mehta_3bhk',
    name: '12 Palm Grove, Koregaon Park, Pune 411001',
    description:
      '3BHK apartment interior fit-out — full scope sheet with sample data.',
    clientName: 'Rahul & Priya Mehta',
    location: '12 Palm Grove, Koregaon Park, Pune 411001',
    startDate: '2026-03-15',
    endDate: '2026-09-30',
    budget: 1850000,
    status: STATUS.ACTIVE,
    scopeDate: '2026-03-10',
    scopeSheet: buildMehtaScopeSheet(),
    createdAt: '2026-03-10T09:00:00.000Z',
    updatedAt: '2026-03-10T09:00:00.000Z',
  },
  {
    id: 'proj_seed_sharma_2bhk',
    name: '88 Whitefield Main Road, Bengaluru 560066',
    description:
      '2BHK villa turnkey interiors — full scope sheet with sample data.',
    clientName: 'Ananya Sharma',
    location: '88 Whitefield Main Road, Bengaluru 560066',
    startDate: '2026-04-01',
    endDate: '2026-10-15',
    budget: 2420000,
    status: STATUS.DRAFT,
    scopeDate: '2026-03-28',
    scopeSheet: buildSharmaScopeSheet(),
    createdAt: '2026-03-28T11:30:00.000Z',
    updatedAt: '2026-03-28T11:30:00.000Z',
  },
  {
    id: 'proj_seed_reddy_4bhk',
    name: 'Plot 21 Jubilee Hills, Hyderabad 500033',
    description:
      '4BHK villa — sample project with bedrooms unlocked via 4BHK unit type.',
    clientName: 'Suresh & Kavya Reddy',
    location: 'Plot 21 Jubilee Hills, Hyderabad 500033',
    startDate: '2026-05-01',
    endDate: '2026-12-20',
    budget: 3200000,
    status: STATUS.ACTIVE,
    scopeDate: '2026-04-20',
    scopeSheet: (() => {
      const sheet = buildMehtaScopeSheet();
      sheet.A.basicInfo = {
        ...sheet.A.basicInfo,
        projectAddress: 'Plot 21 Jubilee Hills, Hyderabad 500033',
        clientNames: 'Suresh & Kavya Reddy',
        propertyType: ['Villa'],
        unitType: '4BHK',
        totalCarpetArea: '2200 sq.ft',
        notes: '4BHK sample — Bedroom 4 included',
      };
      sheet.A.ceilingHeight.bedroom4CeilingHeight = "9'6\"";
      sheet['bedroom-4'] = bedroomBlock(roomDims("13'", "11'", "9'6\""), {
        wardrobeType: ['Normal Wardrobe'],
        wardrobeHeight: ['Full Height'],
        paintNotes: 'Guest bedroom',
      });
      return sheet;
    })(),
    createdAt: '2026-04-20T08:00:00.000Z',
    updatedAt: '2026-04-20T08:00:00.000Z',
  },
];
